import React, { forwardRef } from "react";
import "./BondEvents.css";

import Button from "react-bootstrap/Button";
import ButtonGroup from "react-bootstrap/ButtonGroup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faStop, faEye, faEyeSlash, faArrowUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import Badge from "react-bootstrap/Badge";
import { getBondColor } from "../../../../../../lib/ui/colorUtils.js";
import { ReactComponent as Logo } from "../../Header/logo.svg";
import { getBondEvents, getBondEventsWebSocketUrl } from "../../../../libs/dashboard";
import { useStreamEvents } from "../../../../hooks/useStreamEvents";

const MATE_LOGO_FILTER = 'brightness(0) saturate(100%) invert(44%) sepia(79%) saturate(479%) hue-rotate(182deg) brightness(97%) contrast(93%)';

/**
 * Pre-process API response events for the bond pipeline.
 * Extracts nested payload, copies timestamp/__src, defaults action for topic events.
 */
function preprocessBondApiResponse(events) {
    return events.map(event => {
        if (!event) return null;
        let e = event.payload || event;
        if (e && event.timestamp) e.timestamp = event.timestamp;
        if (e && event.__src) e.__src = event.__src;
        if (e && e.topic && !e.action) e.action = 'message';
        return e;
    }).filter(Boolean);
}

const BondEvents = forwardRef(({ bondId, setEvents, setEventsByRequestId, onToggleVisibility, bondConnectivity, isMate }, ref) => {
    const { isConnected, isConnecting, isLoading, showEvents, handleToggleEvents, connect, disconnect } = useStreamEvents({
        clientId: bondId,
        fetchEvents: getBondEvents,
        getWebSocketUrl: getBondEventsWebSocketUrl,
        defaultSource: 'mqtt',
        preprocessEvents: preprocessBondApiResponse,
        visibilityKey: bondId,
        setEvents,
        setEventsByRequestId,
        onToggleVisibility,
        ref,
    });

    return (
        <div className="d-inline-flex align-items-center gap-2 border rounded px-2 py-1 me-2">
            {/* Bond Logo with Loading Animation */}
            <div
                className={`d-flex align-items-center justify-content-center ${(isConnecting || isLoading) ? 'fa-shake' : isConnected ? 'fa-beat-fade' : ''}`}
                style={{
                    width: '24px',
                    height: '24px',
                    filter: isMate
                        ? MATE_LOGO_FILTER
                        : bondConnectivity?.connected
                            ? 'brightness(0) saturate(100%) invert(68%) sepia(29%) saturate(624%) hue-rotate(89deg) brightness(91%) contrast(84%)'
                            : 'brightness(0) saturate(100%) invert(64%) sepia(40%) saturate(1798%) hue-rotate(321deg) brightness(97%) contrast(90%)'
                }}
                title={bondConnectivity?.connected ? 'Bond Online' : 'Bond Offline'}
            >
                <Logo width={24} height={24} />
            </div>

            {/* Bond ID Badge */}
            <Badge className="text-truncate bondBadge" style={{ '--bond-badge-bg': getBondColor(bondId), color: '#ffffff', minWidth: '105px', maxWidth: '120px' }}>
                {bondId}
            </Badge>

            {/* Button Group for Controls */}
            <ButtonGroup size="sm">
                {/* Start/Stop Live Stream Button */}
                <Button
                    variant={isConnected ? "danger" : "outline-success"}
                    disabled={isConnecting || isLoading}
                    onClick={() => {
                        if (isConnected) disconnect();
                        else if (!isConnecting) { connect(); }
                    }}
                    className="p-0 d-flex align-items-center justify-content-center"
                    style={{ width: '32px', height: '32px', minWidth: '32px' }}
                    title={isConnected ? 'Stop live stream' : 'Start live stream'}
                >
                    <FontAwesomeIcon icon={isConnected ? faStop : faPlay} size="sm" />
                </Button>

                {/* Show/Hide Events Button */}
                <Button
                    variant={showEvents ? "outline-primary" : "outline-secondary"}
                    onClick={handleToggleEvents}
                    className="p-0 d-flex align-items-center justify-content-center"
                    style={{ width: '32px', height: '32px', minWidth: '32px', zIndex: 2 }}
                    title={showEvents ? 'Hide events from this bond' : 'Show events from this bond'}
                >
                    <FontAwesomeIcon icon={showEvents ? faEye : faEyeSlash} size="sm" />
                </Button>

                {/* Open in Palantir Button — not shown for mates */}
                {!isMate && (
                    <Button
                        variant="outline-info"
                        onClick={() => {
                            const palantirUrl = `${window.location.origin}/#/palantir/${bondId}`;
                            window.open(palantirUrl, '_blank');
                        }}
                        className="p-0 d-flex align-items-center justify-content-center"
                        style={{ width: '32px', height: '32px', minWidth: '32px' }}
                        title="Open in Palantir (new tab)"
                    >
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="sm" />
                    </Button>
                )}
            </ButtonGroup>
        </div>
    );
});

BondEvents.displayName = 'BondEvents';

export default BondEvents;
